import type { inferProcedureInput, inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../utils/trpc'

type InitialData = inferProcedureOutput<AppRouter['client']['searchEvaluations']['initialize']>
export type SearchQuery = Omit<inferProcedureInput<AppRouter['client']['searchEvaluations']['search']>, 'userId'>
export type Evaluation = InitialData['evaluations']['data'][number]

export interface SearchEvaluationState {
	evaluations: InitialData['evaluations']
	hasReviewStep: boolean
	searchQuery: SearchQuery
	error: string | null
	loading: boolean
	searchTimeout: NodeJS.Timeout
	initialize: (cycleId: string) => Promise<void>
	search: (query?: SearchQuery) => Promise<void>
	setSearchQuery: (query?: Omit<SearchQuery, 'cycleId'>) => void
	clearError: () => void
}

export const createSearchEvaluationStore = () =>
	create<SearchEvaluationState>()(
		devtools((set, get) => ({
			evaluations: null,
			organization: null,
			hasReviewStep: false,
			searchQuery: null,
			error: null,
			loading: false,

			async initialize(cycleId: string) {
				set({ loading: true, error: null })

				try {
					const { evaluations, hasReviewStep } = await trpcClient.client.searchEvaluations.initialize.query({
						cycleId,
					})

					set({
						evaluations: evaluations,
						hasReviewStep,
						searchQuery: {
							cycleId,
						},
						error: null,
						loading: false,
					})
				} catch (error) {
					set({
						error: (error as Error).message,
						loading: false,
					})
				}
			},

			async search() {
				const { searchQuery } = get()
				// GenericTable's onSearchChange fires on mount and can race with
				// initialize(). Without cycleId the authz middleware throws "Input
				// must contain either cycleId or taskId". Skip the call until
				// initialize has populated searchQuery.
				if (!searchQuery?.cycleId) return

				set({ error: null })

				try {
					const { hasReviewStep, ...evaluations } = await trpcClient.client.searchEvaluations.search.query(searchQuery)

					set({
						evaluations,
						hasReviewStep,
					})
				} catch (error) {
					set({
						error: (error as Error).message,
						loading: false,
					})
				}
			},

			setSearchQuery(query) {
				const { searchQuery, searchTimeout, search } = get()

				if (searchTimeout) {
					clearTimeout(searchTimeout)
				}

				set({
					searchQuery: {
						...searchQuery,
						...query,
					},
					searchTimeout: setTimeout(search, 300),
				})
			},

			clearError() {
				set({ error: null })
			},
		})),
	)

export type SearchEvaluationStoreInstance = ReturnType<typeof createSearchEvaluationStore>
